import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Popconfirm
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { farmerMappingService } from '../../services/farmerMappingService';
import { farmerService } from '../../services/farmerService';
import { companyService } from '../../services/companyService';

const { Option } = Select;

const FarmerMappingList = () => {
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingMapping, setEditingMapping] = useState(null);
    const [farmers, setFarmers] = useState([]);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        fetchMappings();
        fetchFarmersAndCompanies();
    }, []);

    const fetchFarmersAndCompanies = async () => {
        try {
            const [farmersData, companiesData] = await Promise.all([
                farmerService.getAllFarmers(),
                companyService.getAllCompanies()
            ]);
            setFarmers(farmersData);
            setCompanies(companiesData);
        } catch (error) {
            message.error('Failed to fetch farmers and companies');
        }
    };

    const fetchMappings = async () => {
        try {
            setLoading(true);
            const data = await farmerMappingService.getAllMappings();
            setMappings(data);
        } catch (error) {
            message.error('Failed to fetch mappings');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingMapping(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingMapping(record);
        form.setFieldsValue({
            farmer: record.farmer,
            company: record.company,
            status: record.status,
            notes: record.notes
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await farmerMappingService.deleteMapping(id);
            message.success('Mapping deleted successfully');
            fetchMappings();
        } catch (error) {
            message.error('Failed to delete mapping');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingMapping) {
                await farmerMappingService.updateMapping(editingMapping.id, values);
                message.success('Mapping updated successfully');
            } else {
                await farmerMappingService.createMapping(values);
                message.success('Mapping created successfully');
            }
            setModalVisible(false);
            fetchMappings();
        } catch (error) {
            message.error('Failed to save mapping');
        }
    };

    const columns = [
        {
            title: 'Farmer',
            dataIndex: ['farmer_details', 'farmer_name'],
            key: 'farmer',
        },
        {
            title: 'Company',
            dataIndex: ['company_details', 'name'],
            key: 'company',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={
                    status === 'active' ? 'green' :
                    status === 'inactive' ? 'red' :
                    status === 'pending' ? 'orange' : 'default'
                }>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this mapping?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Create Mapping
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={mappings}
                loading={loading}
                rowKey="id"
            />

            <Modal
                title={editingMapping ? 'Edit Mapping' : 'Create Mapping'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <Form.Item
                        name="farmer"
                        label="Farmer"
                        rules={[{ required: true, message: 'Please select a farmer' }]}
                    >
                        <Select>
                            {farmers.map(farmer => (
                                <Option key={farmer.id} value={farmer.id}>
                                    {farmer.farmer_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="company"
                        label="Company"
                        rules={[{ required: true, message: 'Please select a company' }]}
                    >
                        <Select>
                            {companies.map(company => (
                                <Option key={company.id} value={company.id}>
                                    {company.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="notes"
                        label="Notes"
                    >
                        <Input.TextArea />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingMapping ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default FarmerMappingList; 